import { useCallback, useEffect, useRef, useState } from 'react';

const ITEM_GAP = 28;
const MORE_RESERVE = 90;

const useNavOverflow = (items) => {
  const containerRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const measure = useCallback(
    ({ containerWidth, itemWidths }) => {
      if (!containerWidth || itemWidths.length === 0) return;

      // The 90px reserve includes both the More control and its leading gap.
      const availableWidth = containerWidth - MORE_RESERVE;
      let usedWidth = 0;
      let nextVisibleCount = itemWidths.length;

      for (let index = 0; index < itemWidths.length; index += 1) {
        usedWidth += itemWidths[index] + (index > 0 ? ITEM_GAP : 0);

        if (usedWidth > availableWidth) {
          nextVisibleCount = index;
          break;
        }
      }

      setVisibleCount(Math.min(nextVisibleCount, items.length));
    },
    [items.length]
  );

  useEffect(() => {
    setVisibleCount(items.length);
  }, [items]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(() => {
      const itemWidths = Array.from(
        container.querySelectorAll('[data-nav-measure-item]')
      ).map((item) => item.getBoundingClientRect().width);
      const containerWidth = container.getBoundingClientRect().width;

      if (containerWidth > 0 && itemWidths.every((width) => width > 0)) {
        measure({ containerWidth, itemWidths });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [items, measure]);

  const boundedVisibleCount = Math.min(visibleCount, items.length);

  return {
    containerRef,
    visibleItems: items.slice(0, boundedVisibleCount),
    overflowItems: items.slice(boundedVisibleCount),
    __measureForTest: measure,
  };
};

export default useNavOverflow;
